import { FormControl, MenuItem, Select, SelectChangeEvent, Stack } from "@mui/material";
import { ButtonCancel } from "components/buttons";
import { ButtonApplyFilter } from "components/buttons/ButtonApplyFilter";
import { TextFieldFilter } from "components/MprcAdvFilter";
import { AutoCompleteDataModel, AutoCompleteSuggestions, MprcAutoComplete } from "components/MprcAutoComplete";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { CustomFieldType, CustomFieldUsages, CustomFieldValueDto, SearchTextRule, SearchValueRule } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CustomFieldInput } from "views/foundation/customField/components/CustomFieldInput";
import { ICustomFieldFilter } from "views/foundation/customField/components/CustomFieldSelectorDrawer";
import { IAdvFilterPickerProps, IAdvFilterResult } from "views/general/AdvancedFilter/interfaces";

////////////////////////////////////////////////////////////////////////////

export const CustomFieldPicker: React.FC<IAdvFilterPickerProps> = (props) => {

    const { onCancel, onApply } = props;

    const { t } = useTranslation();
    const api = useCoreApi();

    const defaultRuleValue = SearchValueRule.Equals;
    const defaultRuleText = SearchTextRule.Equals;

    const [field, setField] = React.useState<CustomFieldValueDto | undefined>(undefined);
    const [viewFields, setViewFields] = React.useState<CustomFieldValueDto[]>([]);
    const [selectedItem, setSelectedItem] = React.useState<AutoCompleteDataModel[]>([]);
    const [filter, setFilter] = React.useState<Partial<ICustomFieldFilter>>({
        fieldId: undefined,
        name: undefined,
        type: undefined,
        searchValueRule: defaultRuleValue,
        searchTextRule: defaultRuleText,
        value: undefined
    });

    const usage = props.filter.pickerParams as CustomFieldUsages;

    const inputRef = useRef<any>(undefined);
    const inputFieldRef = useRef<any>(null);

    ///
    const getSuggestions = (searchText: string, pageNumber: number, pageSize: number): Promise<AutoCompleteSuggestions<AutoCompleteDataModel>> => {
        searchText = searchText.toLowerCase();
        return new Promise<AutoCompleteSuggestions<AutoCompleteDataModel>>((resolve) => {
            const filterFields = searchText.length > 0
                ? viewFields.filter(opt => opt.label?.toLowerCase().includes(searchText))
                : [...viewFields];

            resolve({
                items: filterFields.map(x => ({
                    key: x.fieldId,
                    label: x.label,
                    canDelete: true,
                })) as AutoCompleteDataModel[],
                totalRows: filterFields.length
            });
        });
    };

    ///
    const setFocus = () => {
        setTimeout(() => {
            inputRef.current?.focus();
        }, 50);
    };

    ///
    useEffect(() => {
        let currentField = selectedItem.length > 0 ? viewFields.find((x) => x.fieldId === selectedItem[0].key) : undefined;

        if (currentField != undefined) {
            let value = undefined;
            if (currentField.fieldType === CustomFieldType.Boolean) value = "false";

            setFilter({
                ...filter,
                fieldId: currentField.fieldId,
                name: currentField.label,
                value,
                type: currentField.fieldType,
                searchValueRule: defaultRuleValue,
                searchTextRule: defaultRuleText
            });
            setField(currentField);
            setFocus();
        }
    }, [selectedItem]);

    ///
    const handleChangeTextRule = (e: SelectChangeEvent<SearchTextRule>) => {
        setFilter({ ...filter, searchTextRule: e.target.value as SearchTextRule, searchValueRule: undefined, value: undefined });
        setFocus();
    };

    ///
    const handleChangeValueRule = (e: SelectChangeEvent<SearchValueRule>) => {
        setFilter({ ...filter, searchValueRule: e.target.value as SearchValueRule, searchTextRule: undefined, value: undefined });
        setFocus();
    };

    ///
    const handleSelectItem = (filter: ICustomFieldFilter | null) => {
        if (filter == null) return onCancel();

        let rule = "";
        if (filter.searchTextRule != undefined) {
            rule = t(`Filter.${SearchTextRule[filter.searchTextRule!]}`, { ns: "shared" }) + ":";
        }

        if (filter.searchValueRule != undefined) {
            rule = t(`Filter.${SearchValueRule[filter.searchValueRule!]}`, { ns: "shared" }) + ":";
        }

        const newFilter: IAdvFilterResult = {
            command: props.filter!.command,
            optionalGroup: props.filter?.optionGroup,
            text: `${filter.name} ${rule} ${filter.formattedValue}`,
            value: filter.value,
            customField: filter,
            quickFilter: null,
            period: null,
            range: null,
            includeDescendants: null
        };

        onApply(newFilter, props.filter);
    };

    useEffect(() => {
        async function getData() {
            try {
                var result = await api.v1.selectors.customFieldsByUsage({ usage: usage, viewUsedInFilters: true });
                setViewFields(result.data);
            } catch (e) {
                MprcRequestSnackbarErrorReport(e);
            }
        }

        if (viewFields.length === 0) getData();
    }, []);

    useEffect(() => {
        (inputFieldRef as any)?.current?.focus();
    }, [inputFieldRef]);

    ///
    useEffect(() => {
        var selOption;
        if (selectedItem.length > 0) {
            selOption = viewFields.find(x => x.fieldId == selectedItem[0].key);
        }
        setField(selOption);
    }, [selectedItem]);

    return (
        <Stack direction="row" alignItems="end" spacing={1}>
            <FormControl sx={{ minWidth: "300px" }}>
                <MprcAutoComplete
                    label={field == undefined ? t("CustomField.CustomFieldFilterDrawer.FieldPlaceholder") : ""}
                    fullWidth
                    autoFocus
                    multiSelect={false}
                    inputRef={inputRef}
                    selectedItems={selectedItem}
                    onChange={(items) => setSelectedItem(items)}
                    onGetSuggestions={getSuggestions}
                />
            </FormControl>
            {
                field != undefined
                    ?
                    (
                        <>
                            {
                                (field.fieldType === CustomFieldType.Text || field.fieldType === CustomFieldType.Uuid) && (
                                    <FormControl sx={{ minWidth: "200px" }}>
                                        <Select value={filter.searchTextRule ?? ""} onChange={handleChangeTextRule}>
                                            {LibUtils.convertEnumToArray(SearchTextRule).map(item =>
                                                <MenuItem value={item.key}>
                                                    {t(`Filter.${item.value}`, { ns: "shared" })}
                                                </MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>
                                )
                            }
                            {
                                (field.fieldType === CustomFieldType.Date ||
                                    field.fieldType === CustomFieldType.Time ||
                                    field.fieldType === CustomFieldType.DateTime ||
                                    field.fieldType === CustomFieldType.Numeric) && (
                                    <FormControl sx={{ minWidth: "200px" }}>
                                        <Select value={filter.searchValueRule ?? ""} onChange={handleChangeValueRule}>
                                            {LibUtils.convertEnumToArray(SearchValueRule).map(item =>
                                                <MenuItem value={item.key}>
                                                    {t(`Filter.${item.value}`, { ns: "shared" })}
                                                </MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>
                                )
                            }
                            {
                                (field.fieldType === CustomFieldType.Text || field.fieldType === CustomFieldType.Uuid) && filter.searchTextRule !== SearchTextRule.Equals
                                    ?
                                    (
                                        <TextFieldFilter
                                            ref={inputRef}
                                            autoFocus
                                            fullWidth
                                            placeholder={t("CustomField.CustomFieldFilterDrawer.SearchTextPlaceholder")}
                                            value={filter.value ?? ""}
                                            onChange={(e) =>
                                                setFilter({
                                                    ...filter,
                                                    value: e.target.value,
                                                    formattedValue: e.target.value
                                                })
                                            }
                                            slotProps={{
                                                input: {
                                                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                                                        if (e.keyCode == 13 && filter.value != undefined)
                                                            handleSelectItem(filter as ICustomFieldFilter);
                                                        if (e.keyCode == 27) handleSelectItem(null);
                                                    }
                                                }
                                            }}
                                        />
                                    )
                                    :
                                    (
                                        <CustomFieldInput
                                            ref={inputRef}
                                            field={{
                                                ...field,
                                                fieldValue: filter.value ?? null,
                                                isRequired: false,
                                                defaultValue: null,
                                                placeholderText: t(field.fieldType == CustomFieldType.Numeric
                                                    ? "CustomField.CustomFieldFilterDrawer.NumberTextPlaceholder"
                                                    : "CustomField.CustomFieldFilterDrawer.SearchTextPlaceholder"),
                                                label: field.fieldType === CustomFieldType.Boolean ? t("CustomField.CustomFieldFilterDrawer.TrueText") : "",
                                                valueSelectionField:
                                                    field.valueSelectionField
                                                        ? { ...field.valueSelectionField, allowMultiValue: true }
                                                        : null,
                                                objectSelectionField:
                                                    field.objectSelectionField
                                                        ? { ...field.objectSelectionField, allowMultiValue: false }
                                                        : null
                                            }}
                                            onChange={(value, formattedValue) => {
                                                setFilter({
                                                    ...filter,
                                                    value,
                                                    formattedValue
                                                });
                                            }}
                                        />
                                    )
                            }
                        </>
                    )
                    :
                    <TextFieldFilter
                        ref={inputRef}
                        fullWidth
                        disabled={true}
                    />
            }
            <Stack direction="row" spacing={1}>
                <ButtonApplyFilter
                    variant="text"
                    disabled={filter.fieldId === undefined || filter.value === undefined}
                    onClick={() => handleSelectItem(filter as ICustomFieldFilter)}
                />
                <ButtonCancel variant="text" onClick={() => onCancel()} />
            </Stack>
        </Stack>
    );
};
